import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { publicUrl } from '../../lib/publicUrl'
import { useToast, humanizeError } from '../shared/Toast'

export default function InvitePeople({ recipient, inviterName, onDone, onCancel }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('family_member')
  const [relationship, setRelationship] = useState('')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState(null) // shown after send (stub-friendly)

  async function submit() {
    if (!name.trim() || !email.trim()) return toast.error('Name and email are required.')
    setBusy(true)
    try {
      const token = crypto.randomUUID()
      const { error } = await supabase.from('care_team_members').insert({
        care_recipient_id: recipient.id,
        invited_name: name.trim(),
        invited_email: email.trim().toLowerCase(),
        role,
        relationship: relationship.trim() || null,
        invite_token: token,
        is_active: true,
      })
      if (error) throw error

      const invite_url = publicUrl(`/invite?token=${token}`)
      // Best-effort email via edge function (stubs to a link if Resend isn't set).
      try {
        await supabase.functions.invoke('send-invite', {
          body: {
            to: email.trim().toLowerCase(),
            invite_url,
            recipient_name: recipient.preferred_name || recipient.full_name,
            inviter_name: inviterName,
            role,
          },
        })
      } catch (e) {
        /* non-fatal — link is shown regardless */
      }
      // eslint-disable-next-line no-console
      console.log('Invite link:', invite_url)
      setLink(invite_url)
      toast.success('Invitation created.')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copied.')
    } catch {
      toast.info('Copy the link manually.')
    }
  }

  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>Invite to care team</h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        {link ? (
          <div className="stack">
            <div className="card card-tint stack" style={{ gap: 8 }}>
              <div style={{ fontWeight: 700 }}>Invitation ready ✓</div>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                We've emailed {name} an invite. You can also share this link directly:
              </p>
              <div className="card" style={{ padding: 10, wordBreak: 'break-all', fontSize: 13, background: 'var(--surface)' }}>{link}</div>
              <button className="btn btn-block" onClick={copy}>Copy link</button>
            </div>
            <button className="btn btn-ghost btn-block" onClick={onDone}>Done</button>
          </div>
        ) : (
          <div className="stack">
            <div className="field"><label className="label">Name *</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah Chen" /></div>
            <div className="field"><label className="label">Email *</label><input type="email" inputMode="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their@email.com" /></div>
            <div className="field">
              <label className="label">Role</label>
              <div className="row wrap" style={{ gap: 8 }}>
                {[['family_member', 'Family member'], ['caregiver', 'Caregiver']].map(([v, l]) => (
                  <button key={v} type="button" className={`chip ${role === v ? 'chip-on' : ''}`} onClick={() => setRole(v)}>{l}</button>
                ))}
              </div>
              <p className="hint">
                {role === 'caregiver' ? 'Can log care and medications; cannot see the care plan or delete documents.' : 'Can view and add most things, and manage medications and appointments.'}
              </p>
            </div>
            <div className="field"><label className="label">Relationship / title</label><input className="input" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. Daughter, Night caregiver" /></div>
            <button className="btn btn-lg btn-block" onClick={submit} disabled={busy}>{busy ? <span className="spinner" /> : 'Send invitation'}</button>
          </div>
        )}
      </div>
    </div>
  )
}
